import { useFitnessStore } from '../../../store/fitnessStore';
import { useCanAccess } from '../../../hooks/useAccess';
import { analyzeProgress } from '../../../services/anthropic';
import { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import PaywallBanner from '../shared/PaywallBanner';

const MOCK_VOLUME = [
  { day: 'Mon', volume: 12000 }, { day: 'Tue', volume: 0 }, { day: 'Wed', volume: 15500 },
  { day: 'Thu', volume: 8000 }, { day: 'Fri', volume: 18000 }, { day: 'Sat', volume: 22000 }, { day: 'Sun', volume: 0 },
];
const MOCK_LIFTS = [
  { week: 'W1', squat: 175, bench: 125, deadlift: 210 }, { week: 'W2', squat: 177, bench: 127, deadlift: 212 },
  { week: 'W3', squat: 180, bench: 130, deadlift: 215 }, { week: 'W4', squat: 182, bench: 132, deadlift: 218 },
  { week: 'W5', squat: 185, bench: 135, deadlift: 220 },
];

const MUSCLE_HEAT = [
  { muscle: 'Chest', volume: 85 }, { muscle: 'Back', volume: 92 }, { muscle: 'Legs', volume: 100 },
  { muscle: 'Shoulders', volume: 70 }, { muscle: 'Arms', volume: 65 }, { muscle: 'Core', volume: 78 },
];

function heatColor(v) {
  if (v >= 90) return '#E8FF47';
  if (v >= 75) return '#FF6B35';
  if (v >= 60) return '#00D4FF';
  return 'rgba(255,255,255,0.15)';
}

export default function ProgressScreen() {
  const profile = useFitnessStore((s) => s.profile);
  const workoutHistory = useFitnessStore((s) => s.workoutHistory);
  const bodyStats = useFitnessStore((s) => s.bodyStats);
  const openModal = useFitnessStore((s) => s.openModal);
  const incrementAIUsage = useFitnessStore((s) => s.incrementAIUsage);
  const canAI = useCanAccess('ai_progress_analysis');
  const [analyzing, setAnalyzing] = useState(false);

  async function handleAnalysis() {
    if (!canAI) { openModal('upgrade'); return; }
    setAnalyzing(true);
    try {
      const analysis = await analyzeProgress({ workoutHistory, bodyStats, profile });
      openModal('plan', {
        planName: `Progress Analysis — Score: ${analysis.overallScore}/100`,
        planDescription: analysis.summary,
        weeklyStructure: [
          { day: 1, dayName: 'Strengths', focus: 'What\'s working', exercises: analysis.strengths?.map((s) => ({ name: s, sets: 0, reps: '', rest: '', intensity: '✅' })) || [] },
          { day: 2, dayName: 'Areas to Improve', focus: 'Focus here', exercises: analysis.weaknesses?.map((w) => ({ name: w, sets: 0, reps: '', rest: '', intensity: '⚠️' })) || [] },
          { day: 3, dayName: 'Recommendations', focus: 'Action items', exercises: analysis.recommendations?.map((r) => ({ name: r, sets: 0, reps: '', rest: '', intensity: '💡' })) || [] },
        ],
        progressionNotes: analysis.predictedOutcome,
      });
      incrementAIUsage('plans');
    } catch {
      alert('Could not analyze progress. Check your API key.');
    } finally {
      setAnalyzing(false);
    }
  }

  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 30, marginBottom: 4 }}>PROGRESS</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Track your evolution</div>

      <div className="fit-grid-2" style={{ marginBottom: 16 }}>
        {[
          { label: 'SQUAT 1RM', value: '185kg', change: '+5kg', color: '#E8FF47' },
          { label: 'BENCH 1RM', value: '135kg', change: '+2.5kg', color: '#FF6B35' },
          { label: 'DEADLIFT 1RM', value: '220kg', change: '+7.5kg', color: '#00D4FF' },
          { label: 'BODYWEIGHT', value: `${profile.weight}kg`, change: 'Stable', color: 'rgba(255,255,255,0.5)' },
        ].map((s) => (
          <div key={s.label} className="fit-card-sm">
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: 1, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: s.color, lineHeight: 1, marginBottom: 2 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: s.change.startsWith('+') ? '#E8FF47' : 'rgba(255,255,255,0.35)' }}>{s.change} this month</div>
          </div>
        ))}
      </div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">WEEKLY VOLUME (KG LIFTED)</div>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={MOCK_VOLUME} barSize={28}>
            <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: '#1C1C1F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 11 }} />
            <Bar dataKey="volume" fill="#E8FF47" fillOpacity={0.8} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">STRENGTH PROGRESS (KG)</div>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={MOCK_LIFTS}>
            <XAxis dataKey="week" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} width={32} />
            <Tooltip contentStyle={{ background: '#1C1C1F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 11 }} />
            <Line type="monotone" dataKey="squat" stroke="#E8FF47" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="bench" stroke="#FF6B35" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="deadlift" stroke="#00D4FF" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          {[{ c: '#E8FF47', l: 'Squat' }, { c: '#FF6B35', l: 'Bench' }, { c: '#00D4FF', l: 'Deadlift' }].map((x) => (
            <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 12, height: 3, borderRadius: 2, background: x.c }} />
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{x.l}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">MUSCLE HEATMAP</div>
        <ResponsiveContainer width="100%" height={100}>
          <BarChart data={MUSCLE_HEAT} layout="vertical" barSize={12}>
            <XAxis type="number" hide domain={[0, 100]} />
            <YAxis type="category" dataKey="muscle" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} width={60} />
            <Bar dataKey="volume" radius={[0, 4, 4, 0]}>
              {MUSCLE_HEAT.map((entry) => (
                <Cell key={entry.muscle} fill={heatColor(entry.volume)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {canAI ? (
        <div style={{ background: 'rgba(232,255,71,0.05)', border: '1px solid rgba(232,255,71,0.15)', borderRadius: 16, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>🤖 AI Progress Analysis</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Deep dive into your 30-day trends. Get specific recommendations from your AI coach.</div>
          <button type="button" onClick={handleAnalysis} disabled={analyzing} className="fit-btn-primary" style={{ opacity: analyzing ? 0.6 : 1 }}>
            {analyzing ? '⏳ Analysing...' : 'Analyse My Progress'}
          </button>
        </div>
      ) : (
        <PaywallBanner feature="AI Progress Analysis" requiredTier="elite">
          <div className="fit-card">
            <div style={{ fontSize: 13, fontWeight: 600 }}>🤖 AI Progress Analysis</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Deep dive into your 30-day trends</div>
          </div>
        </PaywallBanner>
      )}
    </div>
  );
}
